import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Users, Settings, FileText, Bell, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const AdminFeature = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Users,
      title: 'Employee Management',
      description: 'Centralized employee database with profiles, contact info, certifications, skills, emergency contacts, and custom fields.'
    },
    {
      icon: Shield,
      title: 'Role-Based Permissions',
      description: 'Granular access control with customizable roles. Define exactly what each user type can view, edit, approve, or delete.'
    },
    {
      icon: Settings,
      title: 'Company Settings',
      description: 'Configure locations, departments, pay rates, shift types, overtime rules, and compliance policies from one dashboard.'
    },
    {
      icon: FileText,
      title: 'Approval Workflows',
      description: 'Automated approval chains for timesheets, time-off requests, schedule changes, and expense reports with email notifications.'
    },
    {
      icon: Bell,
      title: 'Notification Center',
      description: 'Real-time alerts for schedule changes, clock-in/out events, overtime thresholds, late arrivals, and policy violations.'
    },
    {
      icon: CheckCircle2,
      title: 'Audit Logs',
      description: 'Complete trail of all system changes with who, what, when, and why for compliance, security, and troubleshooting.'
    }
  ];

  const benefits = [
    'Manage unlimited employees across all locations',
    'Reduce admin time by 80% with automation',
    'Enforce policies consistently organization-wide',
    'Bulk actions for mass updates and imports',
    'Custom fields for industry-specific data',
    'Integration with HRIS and payroll systems',
    'Self-service employee portal for updates',
    'Document storage for employee files',
    'Certification tracking and expiration alerts',
    'Advanced filtering and search capabilities'
  ];

  const useCases = [
    {
      title: 'Multi-Location Enterprises',
      description: 'Manage thousands of employees across hundreds of locations from a single dashboard. Set corporate policies while allowing local managers appropriate autonomy. Ensure consistency while maintaining flexibility.'
    },
    {
      title: 'Franchises & Chains',
      description: 'Corporate dashboard gives visibility into all franchise locations. Franchisees get their own admin panel with appropriate restrictions. Maintain brand standards while respecting franchise independence.'
    },
    {
      title: 'Healthcare Systems',
      description: 'Track complex certifications, licenses, and credentials. Ensure proper staff mix and qualifications on every shift. Manage union rules, seniority, and regulatory requirements automatically.'
    },
    {
      title: 'Growing Businesses',
      description: 'Scale seamlessly from 10 to 10,000 employees without changing systems. Add locations, departments, and managers effortlessly. Maintain control while delegating appropriately as you grow.'
    }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Comprehensive Admin Dashboard
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 mb-8 max-w-4xl mx-auto">
              Complete control over your entire workforce from one powerful dashboard. Reduce administrative overhead by 80% while maintaining perfect compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-indigo-500/20 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-indigo-500/30 transition-all"
              >
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
            <img 
              src="/Admin Dashboard.png" 
              alt="Comprehensive Admin Dashboard Interface"
              className="w-full h-auto rounded-xl"
            />
          </div>
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">80%</div>
              <div className="text-gray-600">Less Admin Time</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <div className="text-gray-600">Compliant</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">∞</div>
              <div className="text-gray-600">Scalable</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Complete Admin Control
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Everything you need to manage your workforce efficiently and compliantly from one central hub.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Simplify Complex Workforce Management
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're managing 50 or 50,000 employees, our admin dashboard scales with your business without increasing complexity.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Setup & Configuration</h4>
                    <p className="text-gray-600">Define your organization structure, roles, policies, and approval workflows once.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Import Employees</h4>
                    <p className="text-gray-600">Bulk import existing staff or add individually. System validates data for accuracy.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Delegate Access</h4>
                    <p className="text-gray-600">Grant permissions to managers and supervisors based on their responsibilities.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Monitor & Optimize</h4>
                    <p className="text-gray-600">Track key metrics, receive alerts, and continuously improve operations.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Built for Complex Organizations
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            From small businesses to enterprise organizations, we handle your complexity effortlessly.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Take Control of Your Workforce
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join thousands of organizations managing their workforce efficiently with our admin dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Start Free 14-Day Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default AdminFeature;
